import React from 'react'
import { useSelector } from 'react-redux'
import { Box, EmptyState, EmptyStateTitle, Text } from '@indeed/ifl-components'

import { selectEmployerVenueOptionsById } from 'syft-acp-core/store/employerVenuesFiltered/selectors'

import type { EmployerAccountsVenueResultsProps } from './EmployerAccountsList.types'

const useVenueLabel = (employerID: number, venueID: string) => {
  const venues = useSelector(state => selectEmployerVenueOptionsById(state, employerID))
  const venue = venues.find(v => String(v.id) === String(venueID))

  return venue ? `${venue.id}: ${venue.name}` : `venue ${venueID}`
}

export const EmployerAccountsVenueSummary = ({
  employerID,
  venueID,
  isSearching = false,
  total,
}: EmployerAccountsVenueResultsProps & Readonly<{ total: number }>) => {
  const venueLabel = useVenueLabel(employerID, venueID)
  const managers = `${total.toLocaleString('en-GB')} manager${total === 1 ? '' : 's'}`

  return (
    <Box sx={{ marginBlockEnd: 4 }}>
      <Text as="p">
        <strong>{managers}</strong> {isSearching ? 'at' : 'linked to'} <strong>{venueLabel}</strong>
        {isSearching ? ' match your search' : ''}. Sorted by fewest locations first, so site-level contacts
        are at the top.
      </Text>
    </Box>
  )
}

export const EmployerAccountsVenueEmptyState = ({
  employerID,
  venueID,
  isSearching = false,
}: EmployerAccountsVenueResultsProps) => {
  const venueLabel = useVenueLabel(employerID, venueID)

  return (
    <EmptyState size="sm">
      <EmptyStateTitle>
        {isSearching
          ? `No managers at ${venueLabel} match your search`
          : `No active location-level managers are linked to ${venueLabel}`}
      </EmptyStateTitle>
      {isSearching
        ? 'Clear the Name or E-mail filter to see everyone linked to this venue.'
        : 'Main accounts and company-wide managers can still see every venue. Clear the Venue filter to see them.'}
    </EmptyState>
  )
}
